import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { INTENT, MISSION_PHASE_STATES, MISSION_OPEN_STATES, MissionPhaseIcon, fmtDate, fmtPhaseDate } from "../lib/helpers";
import { PhaseSelector } from "./PhaseSelector";
import { MissionFiles } from "./MissionFiles";
import RdvEditor from "./RdvEditor";
import Conversation, { agencyAuthor } from "./Conversation";
import { ASSISTANT_ID } from "./AssistantAvatar";
import { useConfirm } from "./ConfirmDialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";

const phaseOverdue = (p) => {
  if (!p.planned_end || p.status === "valide") return false;
  const s = String(p.planned_end).split("T")[0];
  const d = new Date(s.length > 7 ? s : `${s}-01`);
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return d < t;
};

export const MissionPanel = ({ m, clients = [], projects = [], modules = [], section, onReload, onNavigate }) => {
  const { ask } = useConfirm();
  const [sp] = useSearchParams();
  const [config, setConfig] = useState(false);
  const [draft, setDraft] = useState([]);
  const [draftMode, setDraftMode] = useState("date");
  const [editBrief, setEditBrief] = useState(false);
  const [briefDraft, setBriefDraft] = useState("");
  const [savingBrief, setSavingBrief] = useState(false);

  const saveBrief = async () => {
    if (!briefDraft.trim()) return toast.error("Le brief ne peut pas être vide");
    setSavingBrief(true);
    try { await api.put(`/admin/missions/${m.id}`, { brief: briefDraft.trim() }); toast.success("Brief mis à jour"); setEditBrief(false); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec de l'enregistrement"); }
    finally { setSavingBrief(false); }
  };

  const reload = () => { if (onReload) return onReload(); };
  const phases = m.wf_phases || [];
  const mode = m.planning_mode || "date";
  const dInputVal = (v, md) => (v ? String(v).split("T")[0].slice(0, md === "month" ? 7 : 10) : "");

  // « Activer » a disparu le 28/09 (lot 5) : ouvrir une phase, c'est lui donner le statut « En cours » par le menu.
  const setPhaseState = async (id, status, feedback) => {
    try {
      const body = { status };
      if (status === "valide_collaborateur") { body.status = "valide"; body.validated_by_type = "collaborateur"; }
      else if (status === "valide_agence") { body.status = "valide"; body.validated_by_type = "agence"; }
      if (feedback !== undefined) body.feedback = feedback;
      await api.put(`/missions/${m.id}/phases/${id}/state`, body);
      toast.success("État enregistré"); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'enregistrement"); reload(); }  // 409 « modifiée ailleurs » : état à jour (30/09)
  };
  const savePhases = async (list, planning_mode) => {
    try { await api.put(`/admin/missions/${m.id}/phases`, { phases: list, planning_mode }); toast.success("Phases mises à jour"); reload(); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Échec de l'enregistrement"); reload(); return false; }
  };
  const setPhaseDate = (id, field, value) => {
    const list = phases.map((p) => (p.id === id ? { ...p, [field]: value || null } : p));
    savePhases(list, mode);
  };
  // L'éditeur de rendez-vous affiche l'erreur ; la mission est rechargée même en cas d'échec (30/09).
  const saveRdv = async (id, body) => { try { await api.put(`/admin/missions/${m.id}/phases/${id}/rdv`, body); } finally { reload(); } };
  const updateLink = async (payload) => {
    try { await api.put(`/admin/missions/${m.id}`, payload); toast.success("Liaison mise à jour"); reload(); }
    catch { toast.error("Échec de la mise à jour"); }
  };
  // Conversation de la mission (01/10) : pastille de phase facultative ; « Marquer comme traité » retire un message de
  // « À traiter » ; la consigne d'une phase « à corriger » est le dernier message « Correction demandée » de la phase.
  const sendMessage = async (text, phaseId) => { await api.post(`/admin/missions/${m.id}/message`, { text, phase_id: phaseId }); await reload(); };
  const markHandled = async (msgId) => {
    try { await api.post(`/admin/missions/${m.id}/messages/${msgId}/handled`); toast.success("Marqué comme traité"); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
    reload();
  };
  const consigneOf = (p) => {
    const last = (m.messages || []).filter((x) => x.kind === "correction" && x.phase_id === p.id).pop();
    return last ? last.text : p.feedback;
  };
  // « À corriger » ouvre d'abord la fenêtre de consigne (arbitrage n° 3 du 01/10) : rien ne part sans elle — jusqu'au
  // 01/10, le menu prévenait le collaborateur avant que la consigne soit saisie. Fermer la fenêtre ne change rien.
  const onStateChange = async (p, value) => {
    if (value === "a_corriger" && p.status !== "a_corriger") {
      const who = m.collaborator_name || "le collaborateur";
      const consigne = await ask({ title: "Demander une correction", label: `Consigne pour ${who}`,
        message: `Phase « ${p.label} » · la consigne est envoyée à ${who} et s'ajoute à la conversation de la mission.`,
        confirmLabel: "Envoyer la demande", multiline: true, select: false });
      if (consigne) setPhaseState(p.id, "a_corriger", consigne);
      return;
    }
    setPhaseState(p.id, value);
  };

  const openConfig = () => { setDraft(phases.map((p) => ({ ...p }))); setDraftMode(mode); setConfig(true); };
  const saveConfig = async () => { if (draft.length === 0) return toast.error("Sélectionnez au moins une phase"); const ok = await savePhases(draft, draftMode); if (ok) setConfig(false); };

  const moduleBlock = (
    <div className="bg-sable border border-marine/10 rounded-sm p-3 mb-4" data-testid="mission-module-admin">
      <div className="text-xs uppercase tracking-wider text-bordeaux mb-2 flex items-center gap-1.5"><Icons.LayoutGrid size={16} /> Module</div>
      <select aria-label="Prestation liée à la mission" value={m.module_key || ""} onChange={(e) => updateLink({ module_key: e.target.value })} data-testid="link-module" className="w-full text-sm border border-marine/20 rounded-sm p-1.5">
        <option value="">— Aucun module —</option>
        {modules.map((mo) => <option key={mo.key} value={mo.key}>{mo.label}</option>)}
      </select>
    </div>
  );

  const linkBlock = (
    <div className="bg-sable border border-marine/10 rounded-sm p-3 mb-4" data-testid="mission-link-admin">
      <div className="text-xs uppercase tracking-wider text-bordeaux mb-2 flex items-center gap-1.5"><Icons.Link2 size={16} /> Liaison entreprise / projet (optionnel)</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <select aria-label="Entreprise liée à la mission" value={m.client_id || ""} onChange={(e) => updateLink({ client_id: e.target.value, project_id: "" })} data-testid="link-client" className="text-sm border border-marine/20 rounded-sm p-1.5">
          <option value="">— Aucune entreprise —</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select aria-label="Projet lié à la mission" value={m.project_id || ""} onChange={(e) => updateLink({ project_id: e.target.value })} data-testid="link-project" className="text-sm border border-marine/20 rounded-sm p-1.5">
          <option value="">— Aucun projet —</option>
          {projects.filter((p) => !m.client_id || p.client_id === m.client_id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      {(m.client_id || m.project_id) && onNavigate && (
        <div className="flex items-center gap-4 mt-2 text-xs">
          {m.client_id && <button onClick={() => onNavigate(`/admin/clients/${m.client_id}`)} data-testid="link-goto-client" className="text-marine hover:underline flex items-center gap-1"><Icons.ArrowRight size={16} /> Voir l'entreprise</button>}
          {m.project_id && <button onClick={() => onNavigate(`/admin/projets/${m.project_id}`)} data-testid="link-goto-project" className="text-marine hover:underline flex items-center gap-1"><Icons.ArrowRight size={16} /> Voir le projet</button>}
        </div>
      )}
    </div>
  );

  const briefBlock = (
    <div className="mb-4" data-testid="mission-brief-admin">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs uppercase tracking-wider text-bordeaux">Brief</div>
        {!editBrief && (
          <button onClick={() => { setBriefDraft(m.brief || ""); setEditBrief(true); }} data-testid="edit-brief-btn"
            className="text-sm border border-marine/25 text-marine rounded-sm px-3 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1.5"><Icons.Pencil size={16} /> Modifier</button>
        )}
      </div>
      {editBrief ? (
        <div>
          <textarea value={briefDraft} onChange={(e) => setBriefDraft(e.target.value)} data-testid="brief-input" rows={6}
            className="w-full border border-marine/20 rounded-sm p-2.5 text-sm text-marine outline-none focus:ring-1 focus:ring-marine" />
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setEditBrief(false)} data-testid="brief-cancel" className="px-4 py-2 text-sm text-marine/80">Annuler</button>
            <button onClick={saveBrief} disabled={savingBrief} data-testid="brief-save" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover transition-colors disabled:opacity-50">{savingBrief ? "Enregistrement…" : "Enregistrer"}</button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-marine whitespace-pre-line">{m.brief}</p>
      )}
    </div>
  );

  const phasesBlock = (
    <>
      <div className="bg-creme border border-marine/15 rounded-sm p-4 mb-4" data-testid="mission-phases-admin">
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs uppercase tracking-wider text-bordeaux">Phases d'avancement de la mission</div>
          <button onClick={openConfig} data-testid="config-phases-btn" className="text-xs border border-marine/30 text-marine rounded-sm px-2 py-1 hover:bg-marine hover:text-white transition-colors flex items-center gap-1"><Icons.Settings2 size={16} /> Configurer</button>
        </div>
        <p className="text-[11px] text-marine/80 mb-3">Une phase validée ouvre la suivante ; le menu de statut ouvre ou reprend n'importe quelle phase · Planification {mode === "month" ? "au mois" : "à la date"}.</p>
        <div className="space-y-2">
          {phases.map((p) => {
            const st = MISSION_PHASE_STATES[p.status] || MISSION_PHASE_STATES.en_cours;
            // Lot 5 (28/09) : plus de pointeur « phase active » — la mise en avant suit le statut :
            // une phase ouverte (ni à venir, ni validée) est mise en avant, une phase à venir est estompée.
            const isOpen = MISSION_OPEN_STATES.includes(p.status);
            const late = phaseOverdue(p);
            return (
              <div key={p.id} data-testid={`phase-row-${p.id}`} className={`rounded-sm border p-3 ${isOpen ? "border-marine/50 bg-sable" : p.status === "a_venir" ? "border-marine/10 carte-a-venir" : "border-marine/10"}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <MissionPhaseIcon status={p.status} />
                    <span className="text-sm font-medium text-marine">{p.label}</span>
                    {p.custom && <span className="text-[9px] uppercase tracking-wider text-bordeaux bg-bordeaux-50 px-1.5 py-0.5 rounded-sm">perso</span>}
                    {late && <span data-testid={`phase-late-${p.id}`} className="inline-flex items-center gap-1 bg-rouge-50 text-rouge border border-rouge/40 px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider">En retard</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Le badge dit l'état, le contrôle dit l'action (décision du 28/09) : ici le menu EST le badge —
                        il porte les couleurs du statut, il n'y a plus de badge séparé qui répète le même mot. */}
                    <select aria-label={`État de la phase ${p.label}`} value={p.status === "valide" ? (p.validated_by_type === "collaborateur" ? "valide_collaborateur" : "valide_agence") : p.status} onChange={(e) => onStateChange(p, e.target.value)} data-testid={`phase-state-${p.id}`} className={`text-[9px] font-medium rounded-sm px-2 py-0.5 pr-6 border ${st.cls}`}>
                      {Object.entries(MISSION_PHASE_STATES).flatMap(([k, v]) => k === "valide"
                        ? [["valide_collaborateur", "Validée par le collaborateur"], ["valide_agence", "Validée par Metara"]]
                        : [[k, v.label]]).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </div>
                </div>
                {p.description && <div className="text-xs text-marine/80 mt-2 whitespace-pre-line">{p.description}</div>}
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {[["planned_start", "Début prévu"], ["planned_end", "Fin prévue"], ["actual_end", "Fin réelle"]].map(([field, lbl]) => (
                    <label key={field} className="text-[10px] text-marine/80 flex flex-col gap-0.5">
                      {lbl}
                      <input type={mode === "month" ? "month" : "date"} value={dInputVal(p[field], mode)} onChange={(e) => setPhaseDate(p.id, field, e.target.value)} data-testid={`phase-${field}-${p.id}`} className="border border-marine/20 rounded-sm p-1 text-xs text-marine" />
                    </label>
                  ))}
                </div>
                {p.status === "a_corriger" && consigneOf(p) && (
                  <div className="mt-3 bg-rouge-50 border border-rouge/30 rounded-sm p-2.5" data-testid={`phase-fb-${p.id}`}>
                    <div className="text-xs font-semibold text-rouge mb-0.5">Consigne envoyée</div>
                    <div className="text-sm text-marine whitespace-pre-line">{consigneOf(p)}</div>
                  </div>
                )}
                <RdvEditor rdv={p} onSave={(body) => saveRdv(p.id, body)} testId={p.id} />
              </div>
            );
          })}
          {phases.length === 0 && <p className="text-sm text-marine/80">Aucune phase. Cliquez sur « Configurer ».</p>}
        </div>
      </div>
      {config && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setConfig(false)}>
          <div className="bg-sable rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="config-phases-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg text-marine">Configurer les phases</h3>
              <button onClick={() => setConfig(false)} aria-label="Fermer" title="Fermer" className="text-marine"><Icons.X size={16} /></button>
            </div>
            <PhaseSelector phases={draft} setPhases={setDraft} planningMode={draftMode} setPlanningMode={setDraftMode} showToValidate={true} />
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setConfig(false)} className="px-4 py-2 text-sm text-marine/80">Annuler</button>
              <button onClick={saveConfig} data-testid="config-phases-save" className="bg-marine text-white rounded-sm px-5 py-2 text-sm">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const filesBlock = (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <MissionFiles mission={m} bucket="shared" files={m.shared_files} reload={reload} />
      <MissionFiles mission={m} bucket="deliverable" files={m.deliverables} reload={reload} />
    </div>
  );

  const labels = Object.fromEntries(phases.map((ph) => [ph.id, ph.label]));
  const conversationBlock = (
    <Conversation
      messages={(m.messages || []).map((x) => ({
        id: x.id, mine: x.sender_role === "admin", text: x.text, created_at: x.created_at,
        author: x.sender_role === "admin" ? agencyAuthor(x.sender_name) : x.sender_name,
        assistant: x.sender_id === ASSISTANT_ID,
        badge: x.phase_id ? { kind: "Phase", label: labels[x.phase_id] || "" } : null,
        kindLabel: x.kind === "correction" ? "Correction demandée" : "",
        awaiting: !!x.awaiting, handled: !!x.handled_at,
      }))}
      canHandle onHandle={markHandled} selectLabel="Phase concernée" options={phases.map((ph) => ({ value: ph.id, label: ph.label }))}
      placeholder="Écrire au collaborateur…" onSend={sendMessage} highlightId={sp.get("message")} testid="mission-conversation" />
  );

  if (section === "apercu") return <>{moduleBlock}{linkBlock}{briefBlock}</>;
  if (section === "phases") return phasesBlock;
  if (section === "fichiers") return filesBlock;
  if (section === "conversation") return conversationBlock;

  return (
    <>
      <div className="flex items-center justify-between gap-3 mb-4 text-sm flex-wrap">
        <span className="text-marine/80">{m.client_name} · Deadline {fmtDate(m.deadline)}</span>
      </div>
      {moduleBlock}
      {linkBlock}
      {phasesBlock}
      {briefBlock}
      {filesBlock}
      <div className="mt-6">{conversationBlock}</div>
    </>
  );
};

export const missionCategory = (m) => {
  if (m.status === "terminee") return "terminee";  // « validee » n'est plus produit ni stocké (état mort retiré le 28/09)
  if (m.status === "a_venir") return "a_venir";
  return "en_cours";
};

export const MISSION_CATEGORIES = {
  a_venir: { label: "À venir", icon: "CalendarClock", color: INTENT.inerte.color },
  en_cours: { label: "En cours", icon: "Loader", color: INTENT.en_cours.color },
  terminee: { label: "Terminée", icon: "CheckCircle2", color: INTENT.acquis.color },
};

export { fmtPhaseDate };
